import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    ContextMenu,
    Switch,
    Card
} from "ncore-ui-kit";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    FileIcon,
    UserIcon
} from "../../anatomy/icons";

const NAME = "ContextMenu";

const USAGE_CODE = `const [lastAction, setLastAction] = useState("");

<ContextMenu
    title="Quarterly report.pdf"
>
    <ContextMenu.Trigger>
        <Card
            onPress={() => setLastAction("Open")}
            isPressable={true}
        >
            <Card.Header>
                <Card.Title>
                    Quarterly report.pdf
                </Card.Title>
                <Card.Description>
                    2.4 MB · Edited yesterday
                </Card.Description>
            </Card.Header>
        </Card>
    </ContextMenu.Trigger>
    <ContextMenu.Content>
        <ContextMenu.Item
            onPress={() => setLastAction("Open")}
            shortcut="enter"
            title="Open"
        />
        <ContextMenu.Separator/>
        <ContextMenu.Item
            onPress={() => setLastAction("Delete")}
            shortcut="mod+backspace"
            title="Delete"
            type="danger"
        />
    </ContextMenu.Content>
</ContextMenu>`;

const TARGET_CODE = `<ContextMenu.Trigger>
    <View
        accessibilityLabel="Message from Ayşe"
        accessible={true}
        tabIndex={0}
    >
        <MessageRow/>
    </View>
</ContextMenu.Trigger>`;

const FILES = [
    "report",
    "budget",
    "deck"
] as const;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("contextMenuPage.triggersTitle"),
        id: "triggers"
    },
    {
        title: t("contextMenuPage.contentTitle"),
        id: "content"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ContextMenuPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        lastAction,
        setLastAction
    ] = useState("");

    const [
        isOffline,
        setIsOffline
    ] = useState(false);

    const [
        isMenuEnabled,
        setIsMenuEnabled
    ] = useState(true);

    const [
        isMenuOpen,
        setIsMenuOpen
    ] = useState(false);

    const files = {
        budget: {
            meta: t("contextMenuPage.fileBudgetMeta"),
            name: t("contextMenuPage.fileBudget")
        },
        report: {
            meta: t("contextMenuPage.fileReportMeta"),
            name: t("contextMenuPage.fileReport")
        },
        deck: {
            meta: t("contextMenuPage.fileDeckMeta"),
            name: t("contextMenuPage.fileDeck")
        }
    };

    const handleAction = (title: string) => setLastAction(title);

    const handleToggleMenu = () => setIsMenuEnabled(!isMenuEnabled);

    const renderTarget = ({
        onPress,
        meta,
        name
    }: {
        onPress?: () => void;
        meta: string;
        name: string;
    }) => <Card
        isPressable={true}
        onPress={onPress}
    >
        <Card.Header>
            <Card.Title>
                {name}
            </Card.Title>
            <Card.Description>
                {meta}
            </Card.Description>
        </Card.Header>
    </Card>;

    const renderFileMenu = (key: (typeof FILES)[number]) => <ContextMenu
        title={files[key].name}
        key={key}
    >
        <ContextMenu.Trigger>
            {
                renderTarget({
                    onPress: () => handleAction(`${t("contextMenuPage.open")} · ${files[key].name}`),
                    meta: files[key].meta,
                    name: files[key].name
                })
            }
        </ContextMenu.Trigger>
        <ContextMenu.Content>
            <ContextMenu.Item
                onPress={() => handleAction(`${t("contextMenuPage.open")} · ${files[key].name}`)}
                title={t("contextMenuPage.open")}
                shortcut="enter"
                icon={FileIcon}
            />
            <ContextMenu.Item
                onPress={() => handleAction(`${t("contextMenuPage.rename")} · ${files[key].name}`)}
                title={t("contextMenuPage.rename")}
                shortcut="F2"
            />
            <ContextMenu.Separator/>
            <ContextMenu.Item
                onPress={() => handleAction(`${t("contextMenuPage.delete")} · ${files[key].name}`)}
                title={t("contextMenuPage.delete")}
                shortcut="mod+backspace"
                type="danger"
            />
        </ContextMenu.Content>
    </ContextMenu>;

    return <Article>
        <ComponentHeader
            lead={t("contextMenuPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("contextMenuPage.overviewText")}
            </Paragraph>
            <Preview
                caption={lastAction ? t("contextMenuPage.lastAction", [
                    lastAction
                ]) : t("contextMenuPage.overviewCaption")}
                isStacked={true}
            >
                <ContextMenu
                    title={files.report.name}
                >
                    <ContextMenu.Trigger>
                        {
                            renderTarget({
                                onPress: () => handleAction(t("contextMenuPage.open")),
                                meta: files.report.meta,
                                name: files.report.name
                            })
                        }
                    </ContextMenu.Trigger>
                    <ContextMenu.Content>
                        <ContextMenu.Item
                            onPress={() => handleAction(t("contextMenuPage.open"))}
                            title={t("contextMenuPage.open")}
                            shortcut="enter"
                            icon={FileIcon}
                        />
                        <ContextMenu.Item
                            onPress={() => handleAction(t("contextMenuPage.rename"))}
                            title={t("contextMenuPage.rename")}
                            shortcut="F2"
                        />
                        <ContextMenu.Sub>
                            <ContextMenu.SubTrigger
                                title={t("contextMenuPage.share")}
                                icon={UserIcon}
                            />
                            <ContextMenu.SubContent>
                                <ContextMenu.Item
                                    onPress={() => handleAction(t("contextMenuPage.copyLink"))}
                                    title={t("contextMenuPage.copyLink")}
                                />
                                <ContextMenu.Item
                                    onPress={() => handleAction(t("contextMenuPage.sendEmail"))}
                                    title={t("contextMenuPage.sendEmail")}
                                />
                            </ContextMenu.SubContent>
                        </ContextMenu.Sub>
                        <ContextMenu.Item
                            onPress={() => handleAction(t("contextMenuPage.copy"))}
                            title={t("contextMenuPage.copy")}
                            shortcut="mod+C"
                        />
                        <ContextMenu.Separator/>
                        <ContextMenu.Item
                            onPress={() => handleAction(t("contextMenuPage.delete"))}
                            title={t("contextMenuPage.delete")}
                            shortcut="mod+backspace"
                            type="danger"
                        />
                    </ContextMenu.Content>
                </ContextMenu>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("contextMenuPage.triggersTitle")}
            id="triggers"
        >
            <Paragraph>
                {t("contextMenuPage.triggersText")}
            </Paragraph>
            <Preview
                caption={t("contextMenuPage.triggersCaption")}
                isStacked={true}
            >
                {FILES.map(renderFileMenu)}
            </Preview>
            <Paragraph
                isMuted={true}
            >
                {t("contextMenuPage.triggersNote")}
            </Paragraph>
            <CodeBlock
                code={TARGET_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("contextMenuPage.contentTitle")}
            id="content"
        >
            <Paragraph>
                {t("contextMenuPage.contentText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <ContextMenu
                    title={t("contextMenuPage.selection")}
                >
                    <ContextMenu.Trigger>
                        <Card
                            aria-label={t("contextMenuPage.selection")}
                            role="group"
                            tabIndex={0}
                        >
                            <Card.Header>
                                <Card.Title>
                                    {t("contextMenuPage.selection")}
                                </Card.Title>
                                <Card.Description>
                                    {t("contextMenuPage.selectionMeta")}
                                </Card.Description>
                            </Card.Header>
                        </Card>
                    </ContextMenu.Trigger>
                    <ContextMenu.Content>
                        <ContextMenu.Group
                            title={t("contextMenuPage.selection")}
                        >
                            <ContextMenu.Item
                                disabledReason={t("contextMenuPage.openOneReason")}
                                title={t("contextMenuPage.open")}
                                isDisabled={true}
                                icon={FileIcon}
                            />
                            <ContextMenu.Item
                                title={t("contextMenuPage.downloadZip")}
                            />
                            <ContextMenu.Item
                                title={t("contextMenuPage.moveTo")}
                            />
                        </ContextMenu.Group>
                        <ContextMenu.Separator/>
                        <ContextMenu.CheckboxItem
                            title={t("contextMenuPage.keepOffline")}
                            onChange={setIsOffline}
                            isChecked={isOffline}
                        />
                        <ContextMenu.Separator/>
                        <ContextMenu.Item
                            title={t("contextMenuPage.deleteSelected")}
                            type="danger"
                        />
                    </ContextMenu.Content>
                </ContextMenu>
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("contextMenuPage.statesText")}
            </Paragraph>
            <Preview
                caption={isMenuOpen ? t("contextMenuPage.menuOpen") : t("contextMenuPage.menuClosed")}
                isStacked={true}
            >
                <Switch
                    title={t("contextMenuPage.menuEnabled")}
                    onPress={handleToggleMenu}
                    isChecked={isMenuEnabled}
                />
                <ContextMenu
                    onOpenChange={setIsMenuOpen}
                    isDisabled={!isMenuEnabled}
                    title={files.budget.name}
                >
                    <ContextMenu.Trigger>
                        {
                            renderTarget({
                                meta: files.budget.meta,
                                name: files.budget.name
                            })
                        }
                    </ContextMenu.Trigger>
                    <ContextMenu.Content>
                        <ContextMenu.Item
                            title={t("contextMenuPage.open")}
                            icon={FileIcon}
                        />
                        <ContextMenu.Item
                            title={t("contextMenuPage.rename")}
                        />
                    </ContextMenu.Content>
                </ContextMenu>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("contextMenuPage.a11yTrigger"),
                    t("contextMenuPage.a11yKeyboard"),
                    t("contextMenuPage.a11yPointer"),
                    t("contextMenuPage.a11yName"),
                    t("contextMenuPage.a11yNative"),
                    t("contextMenuPage.a11yTouch"),
                    t("contextMenuPage.a11yDisabled"),
                    t("contextMenuPage.a11yWarn")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("contextMenuPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("contextMenuPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("contextMenuPage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("contextMenuPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("contextMenuPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("contextMenuPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    ContextMenu.Trigger
                </InlineCode>
                {" "}
                {t("contextMenuPage.partTriggerText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("contextMenuPage.propTriggerChildren"),
                        type: "ReactElement",
                        name: "children"
                    },
                    {
                        description: t("contextMenuPage.propTriggerStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "style"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("contextMenuPage.propTriggerCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("contextMenuPage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ContextMenuPage;
